// File: packages/api-client/src/client.ts
// Version: 2026.8.7 | ForgeTrack authenticated API client
// v2026.8.7: Pass `account` to the step-up popup. Its absence meant Entra got
//            no login_hint and showed a blank sign-in every time, which also
//            defeated max_age (no account context to SSO against).
// v2026.8.6: Step-up now requests OIDC max_age instead of prompt=login, so
//            Entra re-prompts only when the sign-in is genuinely stale and
//            SSOs through otherwise. The window is read from the 403's
//            requiredAgeMinutes and tightened to 60% to avoid a re-reject.
// v2026.8.5: Log the step-up failure reason instead of swallowing it.
// v2026.8.4: Step-up popup now passes prompt via MSAL's native field instead
//            of extraQueryParameters (workforce tenant, not CIAM).
// v2026.8.3: Removed the hardcoded VITE_API_URL / VITE_API_AUDIENCE fallbacks.
//            The audience default still named api://c88de640-… — an app
//            registration in the Entra tenant Microsoft is deleting — and it
//            was baked into a shipped radar bundle. See ./env.ts.
// v2026.8.2: Added the step-up MFA (403) recovery interceptor that
//            RequireFreshMfaAttribute has always documented as existing here
//            but that was never actually built. A `step_up_required` response
//            now forces a fresh interactive sign-in and replays the original
//            request once. `step_up_unsupported` is deliberately NOT handled:
//            it is returned to API-key callers, who can never satisfy a
//            step-up, so retrying would loop forever.
// v2026.4.1: Original version.

import { useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@forgetrack/auth';
import { requiredEnv } from './env';
import type { ApiInstance } from './types';

const LOCALE_COOKIE = 'ft-locale';

function readLocaleCookie(): string {
  if (typeof document === 'undefined') return 'en';
  const match = document.cookie.match(new RegExp(`${LOCALE_COOKIE}=([^;]+)`));
  return match?.[1] ?? 'en';
}

/**
 * Primary API hook. Returns a factory that creates authenticated Axios instances.
 * Automatically injects: Bearer token, Accept-Language.
 *
 * Usage:
 *   const { createApi } = useApiClient();
 *   const api = await createApi();
 *   const { data } = await api.get('/api/v1/projects');
 */
export function useApiClient() {
  const { instance: msalInstance } = useAuth();

  const baseUrl = useMemo(() => {
    // @ts-ignore — Vite replaces import.meta.env.VITE_* at bundle time
    return requiredEnv('VITE_API_URL', import.meta.env?.VITE_API_URL);
  }, []);

  const createApi = useCallback(async (): Promise<ApiInstance> => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) throw new Error('Not authenticated');

    // Vite statically replaces literal `import.meta.env.VITE_*` at bundle time.
    // Do NOT alias import.meta — Vite's replacement is pattern-based.
    // @ts-ignore — Vite injects VITE_* at build time; tsc doesn't see them
    const audience: string = requiredEnv('VITE_API_AUDIENCE', import.meta.env?.VITE_API_AUDIENCE);
    const scopes = [`${audience}/.default`];
    const response = await msalInstance.acquireTokenSilent({
      scopes,
      account: accounts[0],
    });

    const locale = readLocaleCookie();

    const api = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${response.accessToken}`,
        'Accept-Language': locale,
      },
    });

    // Response interceptor: handle 401 gracefully
    api.interceptors.response.use(
      (res) => res,
      async (error) => {
        if (error.response?.status === 401) {
          // Try token refresh via popup
          try {
            // account matters here for the same reason as the step-up below:
            // without it Entra gets no login_hint and shows a blank sign-in.
            const refreshed = await msalInstance.acquireTokenPopup({ scopes, account: accounts[0] });
            error.config.headers['Authorization'] = `Bearer ${refreshed.accessToken}`;
            return axios(error.config);
          } catch {
            // Refresh failed — redirect to login
            window.location.href = '/';
          }
        }

        // 403 + step_up_required: the endpoint is gated by [RequireFreshMfa]
        // and our token's auth_time is missing or stale. A silent refresh
        // canNOT fix that — it replays the same upstream auth event — so we
        // force an interactive re-authentication and replay the request once.
        //
        // The retry flag matters: without it, a server that still rejects
        // (e.g. the auth_time optional claim isn't configured on the API app
        // registration) would pop the login window on every retry forever.
        if (
          error.response?.status === 403 &&
          error.response?.data?.code === 'step_up_required' &&
          error.config &&
          !error.config.__stepUpRetried
        ) {
          error.config.__stepUpRetried = true;

          // Ask for auth freshness with OIDC `max_age` rather than forcing a
          // full sign-in with prompt=login. Entra re-prompts ONLY when the
          // existing authentication is older than max_age, and SSOs through
          // silently otherwise — so a user who signed in recently sees the
          // popup flash and close instead of retyping an email, a password
          // and an MFA challenge. It is also what the server actually wants:
          // RequireFreshMfa checks the age of auth_time, not whether a prompt
          // was shown.
          //
          // The server tells us its window in the 403 body (requiredAgeMinutes
          // from RequireFreshMfaAttribute), so read it instead of hardcoding.
          // We deliberately ask for a TIGHTER window than the server enforces:
          // Entra evaluates freshness when it issues the token, the server
          // when it receives it, and asking for exactly the server's window
          // would let a token that was 899s old at issue arrive at 901s and be
          // rejected again — a guaranteed second popup. 60% leaves real slack.
          //
          // max_age goes through extraQueryParameters because MSAL v5 has no
          // native field for it. That is safe here in a way `prompt` was not:
          // MSAL never emits max_age itself, so there is no risk of sending a
          // duplicate query parameter.
          const requiredMinutes = Number(error.response?.data?.requiredAgeMinutes) || 15;
          const maxAgeSeconds = Math.max(60, Math.floor(requiredMinutes * 60 * 0.6));

          try {
            const steppedUp = await msalInstance.acquireTokenPopup({
              scopes,
              // Pass the signed-in account. Without it MSAL sends no
              // login_hint/sid, so Entra cannot tell WHO is re-authenticating
              // and renders a blank sign-in page — the user retypes their email
              // and password on every single step-up, and max_age can never
              // SSO through because there is no account context to match a
              // session against. With the account supplied Entra either
              // silently reuses the session (fresh enough) or asks only for
              // the password/MFA of a known user.
              account: accounts[0],
              extraQueryParameters: { max_age: String(maxAgeSeconds) },
            });
            error.config.headers['Authorization'] = `Bearer ${steppedUp.accessToken}`;
            return axios(error.config);
          } catch (stepUpError) {
            // Popup dismissed, timed out, or re-auth failed. Surface WHY in the
            // console before falling through — swallowing this silently made a
            // popup-timeout look identical to a plain permission denial, which
            // cost real debugging time. The original 403 is still rejected so
            // the caller shows the server's message.
            console.error('[ForgeTrack] Step-up authentication failed:', stepUpError);
          }
        }

        return Promise.reject(error);
      },
    );

    return api;
  }, [msalInstance, baseUrl]);

  return { createApi };
}
